import {List} from 'immutable';
import isEmpty from 'lodash/isEmpty';
import pickBy from 'lodash/pickBy';
import CoachingReviewQuestion, {
  RESPONDENT_TYPE,
  validate as validateQuestion,
} from 'records/CoachingReviewTypeQuestion';
import {requiredMessage} from 'utils/const';
import sum from 'utils/sum';
import partitionBy from 'utils/partitionBy';
import CoachingMeetingCategory from 'records/CoachingMeetingCategory';
import {Data, Int, RecordProps} from '@store/@types/util';

const WEIGHT_TOTAL = 100;

/**
 * Redistribute weights on blur of call weight selection or any individual
 * question selection.
 *
 * We preserve the user's current selection and adjust the rest down the proper
 * amount if they're over 100%.
 *
 * Preserve the user's intent as much as possible.
 *
 * https://docs.google.com/spreadsheets/d/11vjqo7zAGoSOBVc4n0GgV-xDPRYPZTKV3rz4AU4N36g/edit?usp=sharing
 *
 * @param reviewType - the model to update
 * @param editedQuestionIndices - which questions have been edited? Try to preserve them if possible. If not, we'll
 *  return a flag to let the caller reset the list
 * @param hasEditedCallWeights - has the user edited the call weights? Same idea as above
 * @param lastEditWasCallWeights - was the last edit call weights? We'll try to preserve them, even if we need to
 *  blow away the weights
 * @param lastQuestionEditIndex - same ideas as above - we'll try to preserve the last q edited
 * @return [updatedModel, blowAwayEdits] - the updated model with weights redistributed, and a boolean to indicate
 *  if we needed to blow away user edited weights in order to redistribute
 */
export function redistributeWeights(
  reviewType,
  editedQuestionIndices,
  hasEditedCallWeights,
  lastEditWasCallWeights,
  lastQuestionEditIndex
) {
  // make a list of tuples of object merge path, current weight, and current user selection
  const undistributedScratch = List([
    reviewType.includesCalls
      ? {
          path: 'callWeighting',
          value: reviewType.callWeighting,
          hasEdited: hasEditedCallWeights,
        }
      : null,
    ...reviewType.questions.map((q, i) =>
      q.isText()
        ? null
        : {
            path: ['questions', i, 'weight'],
            value: q.weight,
            hasEdited: editedQuestionIndices.contains(i),
          }
    ),
  ]).filter(s => s);

  const [toLeaveAlone, theRest] = partitionBy(undistributedScratch, s => s.hasEdited);

  const restTotal = sum(theRest.map(s => s.value));
  const weightToFill = toLeaveAlone.size === 0 ? 100 : 100 - sum(toLeaveAlone.map(q => q.value));

  // we can't preserve the current state of the world if the amount of weight
  // we need to distribute amongst the unedited questions is negative. In that case,
  // try again only preserving the single last edit the user made.
  const cantPreserveEdits = weightToFill < 0;
  if (cantPreserveEdits) {
    const [fromScratch] = redistributeWeights(
      reviewType,
      lastQuestionEditIndex === null ? List() : List([lastQuestionEditIndex]),
      lastEditWasCallWeights,
      false,
      null
    );
    return [fromScratch, true];
  }

  const redistributed = redistributeBuckets(theRest, weightToFill, restTotal, hasEditedCallWeights);

  const roundingError = weightToFill - sum(redistributed.map(s => s.value));
  redistributed.size > 0 && (redistributed.get(redistributed.size - 1).value += roundingError);

  // now we apply all the diffs.
  return [
    redistributed.reduce((rt, c) => {
      return Array.isArray(c.path) ? rt.setIn(c.path, c.value) : rt.set(c.path, c.value);
    }, reviewType),
    false,
  ];
}

/**
 * Redistribute weighting into the buckets
 *
 * @param toRedistribute - scratch list to redistribute
 * @param weightToFill - weight we need to get to
 * @param totalPoints - weight we have right now
 * @param hasEditedCallWeights - if the user has edited call weights, preserve
 *  their intention and redistribute proportionally. otherwise equally.
 *
 * @return updatedScratch - redistributed weights based on the strategy
 */
function redistributeBuckets(toRedistribute, weightToFill, totalPoints, hasEditedCallWeights) {
  // a single proportional unit in the weights we have yet to fill.
  const ratio = totalPoints === 0 ? 1 : weightToFill / totalPoints;

  if (hasEditedCallWeights) {
    return toRedistribute.map(r => {
      return {...r, value: Math.round(r.value * ratio)};
    });
  } else {
    return toRedistribute.map(r => {
      return {...r, value: Math.round(weightToFill / toRedistribute.size)};
    });
  }
}

export function validate(reviewType) {
  const name = isEmpty(reviewType.name) && requiredMessage;

  // includeCalls is false, but there are no questions
  const noQuestions =
    !reviewType.includesCalls &&
    reviewType.questions.isEmpty() &&
    'Please add questions or include calls in this session.';

  // If any question returned errors then attach the list of errors
  const errors = reviewType.questions.map(validateQuestion);
  const questionErrors = errors.some(e => !isEmpty(e)) && errors;

  const [weightTooHigh, weightTooLow] = validateWeight(reviewType);

  return pickBy({
    name,
    noQuestions,
    weightTooHigh,
    weightTooLow,
    questionErrors,
  });
}

/**
 * This shouldnt usually be necessary, but there are edge cases where you
 * can still end up with weight > 100 (for ex, turn off call focused session,
 * add some qs, turn call focused session back on).  Keep this here for safety
 *
 * @param reviewType
 * @return {*[]|boolean[]}
 */
function validateWeight(reviewType) {
  if (
    !reviewType.includesCalls &&
    reviewType.questions.size > 0 &&
    reviewType.questions.every(q => q.isText())
  ) {
    return [false, false];
  }

  const totalWeight = sum(
    reviewType.questions
      .filterNot(q => q.isText())
      .map(q => q.weight)
      .push(reviewType.includesCalls ? reviewType.callWeighting : 0)
  );

  // Total weight must be 100
  const weightTooHigh =
    totalWeight > WEIGHT_TOTAL &&
    `You have over 100% weight allocated.
     Please reduce the overall weight by ${totalWeight - WEIGHT_TOTAL}%`;

  const weightTooLow =
    totalWeight < WEIGHT_TOTAL &&
    `You have under 100% weight allocated.
     Please assign the remaining ${WEIGHT_TOTAL - totalWeight}%
     to questions or calls.`;

  return [weightTooHigh, weightTooLow];
}

/**
 * Used when creating a plan. Make sure there is at least one.
 */
export function validateReviewTypeIds(ids) {
  return ids.size === 0 && requiredMessage;
}

export type CoachingReviewTypeId = Int;
interface CoachingReviewTypeInterface {
  readonly id?: CoachingReviewTypeId;
  readonly name: string;
  readonly includesCalls: boolean;
  readonly questions: List<any>; // TODO
  readonly callWeighting: number;
  readonly meetingCategoryId?: number;
  // not present from the API, but filled when using the normal store method
  readonly meetingCategory?: CoachingMeetingCategory;
  readonly isDefault: boolean;
  readonly isInUseByActivePlans: boolean;
}

export default class CoachingReviewType extends Data<CoachingReviewTypeInterface>({
  id: undefined,
  name: '',
  includesCalls: true,
  questions: List(),
  callWeighting: 0,
  meetingCategoryId: undefined,
  meetingCategory: undefined,
  isDefault: false,
  isInUseByActivePlans: false,
}) {
  constructor(object: RecordProps<CoachingReviewType>) {
    super({
      ...object,
      questions: List(object.questions?.map(q => new CoachingReviewQuestion(q)) ?? []),
    });
  }

  hasRepQuestions() {
    return this.questions.some(q => q.respondent === RESPONDENT_TYPE.REP);
  }
}
